"use client";
import React, { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { z } from "zod";
import PhoneInput from "@/app/login/components/phone-input";
import { authService } from "@/app/api/service/auth";
import OtpInput from "@/app/login/components/otp-input";
import DetermineEmailAuth from "@/app/login/components/determine-email-auth";
import { SIGNUP_QUESTIONS, subDomains } from "@/app/utils/constants";
import QuestionsInput from "@/app/login/components/question-input";
import { emailValidation, nameValidation } from "@/app/model/login";
import LoadingIcon from "@/app/assets/icons/loading";
import {
    SIGNUP_NPS_QUES_STATUS,
    SIGNUP_STEPS,
    SIGNUP_TYPE,
    SIGNUP_URL_PARAMS,
} from "@/app/enums/signup";
import { initBranch, trackEvent } from "@/app/utils/branchio";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
import {
    clearRedirectTo,
    // getCleanDomain,
    getRedirectTo,
    getReleventDomain,
    isValidRedirectTo,
} from "@/app/utils/helper";
import { sendMetaConversionEvent } from "@/app/utils/metaConversion";
import { getGTM } from "@/app/utils/gtm";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import loginBg from "@/app/assets/images/login-bg.png";
// import thumbPrint from "@/app/assets/images/thumb-print.png";
// import Image from 'next/image';
import LoginImageCarousel from "@/app/components/shared/login-image-carousel";
import MobileImageCarousel from "@/app/components/shared/mobile-image-carousel";
import { useCleverTapUser, useCleverTapEvents } from "@/app/hooks/use-clevertap";
import { DirectPaths } from "@/config/routes";

interface UtmParams {
    utm_source?: string | null;
    utm_medium?: string | null;
    utm_campaign?: string | null;
    utm_term?: string | null;
    referrer_source?: string | null;
    utm_content?: string | null;
    _branch_match_id?: string | null;
    _branch_referrer?: string | null;
}

interface UserDetails {
    email: string;
    userId: string;
    verified: boolean;
    phoneVerified: string;
    phone: string;
    name: string;
    emailVerified: boolean;
    userType: string;
    referrer: string;
    success: boolean;
}

interface AuthPayload {
    cCode?: string;
    mobile?: string;
    source?: string;
    generateOtp?: boolean;
    captchaToken?: string;
    [key: string]: string | boolean | undefined;
}

interface OTPAuthPayload {
    cCode?: string;
    email?: string;
    mobile?: string;
    source?: string;
    generateOtp?: boolean;
    captchaToken?: string;
    [key: string]: string | boolean | undefined;
}

interface Payload extends AuthPayload, OTPAuthPayload {}

interface FeedbackPayload {
    qId: string;
    feedback: {
        npsFeedback: string[];
        status: string;
        surveyDate: number;
    };
}

interface AuthResponse {
    success: boolean;
    message?: string;
}

interface LoginContainerProps {
    searchParams: URLSearchParams;
    recaptchaEnabled?: boolean;
    api?: string; // pass api to overwrite existing api.
}

type CleverTapAuthEventType = "login" | "signup";
type CleverTapAuthMethod = "phone" | "email" | "google";

const LoginContainer = ({ searchParams, recaptchaEnabled }: LoginContainerProps) => {
    const router = useRouter();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const currentDomain = getReleventDomain(/* true */);
    const redirectUrl = searchParams.get("redirect") || "";

    // CleverTap hooks for user tracking and events
    const { onUserLogin } = useCleverTapUser();
    const { trackLogin, trackSignUp, cleverTapTrackEvent } = useCleverTapEvents();

    const [phoneNumber, setPhoneNumber] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [name, setName] = useState<string>("Investor");
    const [urlRefCode, setUrlRefCode] = useState<string | null>(null);
    const [isLoggedin, setIsLoggedin] = useState<boolean>(false);
    const [viaPhone, setViaPhone] = useState<boolean>(false);
    const [userDetails, setUserDetails] = useState<UserDetails>();
    const [utmSource, setUtmSource] = useState<UtmParams>({});
    const [isNewUser, setIsNewUser] = useState<boolean>(false);
    const [currentStep, setCurrentStep] = useState<
        | SIGNUP_STEPS.PHONE
        | SIGNUP_STEPS.PHONEOTP
        | SIGNUP_STEPS.EMAIL
        | SIGNUP_STEPS.EMAILOTP
        | SIGNUP_STEPS.QUESTION1
        | SIGNUP_STEPS.QUESTION2
        | SIGNUP_STEPS.QUESTION3
    >(SIGNUP_STEPS.PHONE);
    const [resetOtp, setResetOtp] = useState<boolean>(false);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
    const [otpErrorMessage, setOtpErrorMessage] = useState<string | null>(null); // Add error state
    const [emailError, setEmailError] = useState<string | null>(null); // Error for email
    const [nameError, setNameError] = useState<string | null>(null); // Error for name
    const [buttonLoader, setButtonLoader] = useState<boolean>(false);
    const [checkLogin, setCheckLogin] = useState<boolean>(true);
    const [hideOtpScreen, setHideOtpScreen] = useState<boolean>(false);
    const [firstResponse, setFirstResponse] = useState<string | null>(null);
    const [isCarouselFrozen, setIsCarouselFrozen] = useState<boolean>(false);
    const [pendingAuthTrack, setPendingAuthTrack] = useState<{
        type: CleverTapAuthEventType;
        method: CleverTapAuthMethod;
    } | null>(null);
    const cCode: string = "91";
    const selectedResponses: Record<string, string[]> = {}; // Store selected items locally

    // redirect to relevent domain
    const redirectToDomain = () => {
        const href = (typeof window !== "undefined" && window.location.href) || "";

        if (href.includes(subDomains.FD)) {
            window.location.href = `${currentDomain}${DirectPaths.FD_POSTFIX_PATH}`;
        } else if (href.includes(subDomains.GLOBAL)) {
            window.location.href = `${currentDomain}${DirectPaths.GLOBAL + DirectPaths.US_STOCKS}`;
        } else if (href.includes(subDomains.BULLION)) {
            window.location.href = `${currentDomain}${DirectPaths.BULLION_POSTFIX_PATH}${DirectPaths.GOLD}`;
        } else if (href.includes(subDomains.ALTS)) {
            window.location.href = `${currentDomain + "/unlisted-shares"}`;
        } else {
            window.location.href = `${currentDomain}${DirectPaths.PLATFORM}`;
        }
    };
    // Existing useEffects remain the same
    useEffect(() => {
        const fetchData = async () => {
            setCheckLogin(true); // Start loading

            const queryParams = new URLSearchParams(window.location.search);

            // Capture referral code (if present)
            setUrlRefCode(queryParams.get(SIGNUP_URL_PARAMS.REF_CODE_PARAM));
            setPhoneNumber("");

            // Build UTM object
            const utmObj: UtmParams = {
                utm_source: queryParams.get(SIGNUP_URL_PARAMS.UTM_SOURCE) ?? "",
                utm_medium: queryParams.get(SIGNUP_URL_PARAMS.UTM_MEDIUM) ?? "",
                utm_campaign: queryParams.get(SIGNUP_URL_PARAMS.UTM_CAMPAIGN) ?? "",
                utm_term: queryParams.get(SIGNUP_URL_PARAMS.UTM_TERM) ?? "",
                referrer_source: queryParams.get(SIGNUP_URL_PARAMS.REFERRER_SOURCE) ?? "",
                utm_content: queryParams.get(SIGNUP_URL_PARAMS.UTM_CONTENT) ?? "",
                _branch_match_id: queryParams.get(SIGNUP_URL_PARAMS.BRANCH_MATCH_ID) ?? "",
                _branch_referrer: queryParams.get(SIGNUP_URL_PARAMS.BRANCH_REFERRER) ?? "",
            };

            const storedParams = window.localStorage.getItem(SIGNUP_URL_PARAMS.UTM_ICM_PARAM);

            if (!storedParams) {
                // Save first-time UTMs
                window.localStorage.setItem(
                    SIGNUP_URL_PARAMS.UTM_ICM_PARAM,
                    JSON.stringify(utmObj),
                );
                setUtmSource(utmObj);
            } else {
                try {
                    // Make sure it's valid JSON before parsing
                    const parsed = JSON.parse(storedParams);
                    setUtmSource(parsed);
                } catch {
                    console.warn("Invalid UTM data in localStorage, resetting:", storedParams);
                    window.localStorage.setItem(
                        SIGNUP_URL_PARAMS.UTM_ICM_PARAM,
                        JSON.stringify(utmObj),
                    );
                    setUtmSource(utmObj);
                }
            }

            await autoLogin();
            setCheckLogin(false);
        };

        void fetchData();
    }, []);

    useEffect(() => {
        const verifyUser = async () => {
            if (isLoggedin) {
                setCheckLogin(true); // Start loading for user verification

                await authService.switchUser();
                const response = await authService.getUserDetails();

                if (response?.success) {
                    const isRedirectingExistingUser = response.verified && response.phoneVerified;

                    // Track user login in CleverTap with user profile
                    onUserLogin(
                        {
                            identity: response.userId,
                            name: response.name,
                            email: response.email,
                            phone: response.phone ? `+91${response.phone}` : undefined,
                        },
                        !isRedirectingExistingUser,
                    );

                    // User identification must be established before auth tracking events
                    if (pendingAuthTrack) {
                        if (pendingAuthTrack.type === "login") {
                            trackLogin(pendingAuthTrack.method);
                        } else {
                            trackSignUp(pendingAuthTrack.method);
                        }
                        setPendingAuthTrack(null);
                    }

                    if (isRedirectingExistingUser) {
                        const redirectTo = getRedirectTo();

                        clearRedirectTo();

                        if (redirectUrl) {
                            router.push(redirectUrl);
                        } else if (isValidRedirectTo(redirectTo)) {
                            window.location.href = String(redirectTo);
                            return;
                        } else {
                            redirectToDomain();
                            return;
                        }
                    }
                    setUserDetails({ ...response });
                } else {
                    setIsLoggedin(false);
                }

                setCheckLogin(false); // Stop loading after fetching user details
            }
        };

        void verifyUser();
    }, [isLoggedin, pendingAuthTrack, onUserLogin, trackLogin, trackSignUp]);

    useEffect(() => {
        if (userDetails && !userDetails.phoneVerified) {
            setCurrentStep(SIGNUP_STEPS.PHONE);
        }
        if (userDetails && (!userDetails.verified || !userDetails.name)) {
            setCurrentStep(SIGNUP_STEPS.EMAIL);
        }
    }, [userDetails]);

    const handleGoogleSignUp = () => {
        setPendingAuthTrack({
            type: "signup",
            method: "google",
        });
        setCurrentStep(SIGNUP_STEPS.QUESTION1);
        setIsLoggedin(true);
    };

    // Existing methods remain the same
    async function autoLogin() {
        const authStatus = await authService.getAuthStatus();
        if (authStatus) {
            setIsLoggedin(true);
            router.push(redirectUrl);
        }
    }

    const trackGtmEvent = () => {
        getGTM().then((gtm) => {
            gtm.dataLayer({
                dataLayer: {
                    event: "US_Positive_Signup_GTM",
                },
            });
        });
    };

    const handleQuestionSubmit = async (qid: string, selectedItems: string[]) => {
        const eventId = crypto.randomUUID(); // Generate a unique event ID
        selectedResponses[qid] = selectedItems;

        if (qid === SIGNUP_QUESTIONS[0].qid && selectedItems.length > 0) {
            setFirstResponse(selectedItems[0]);
        }

        const payload: FeedbackPayload = {
            qId: qid,
            feedback: {
                npsFeedback: selectedItems,
                status: SIGNUP_NPS_QUES_STATUS.SUBMITTED,
                surveyDate: Date.now(),
            },
        };

        try {
            setButtonLoader(true);
            const response: AuthResponse = await authService.submitFeedback(payload);

            if (response && response.success) {
                const eventMapping: string[] = [
                    "Signup_product_selected",
                    "Signup_Amount_to_be_invested",
                    "Signup_Heard_us",
                ];
                const eventName: string = eventMapping[currentQuestionIndex] || "";

                // Fire analytics events without waiting (non-blocking)
                fireAnalyticsEvents(
                    eventName,
                    email,
                    phoneNumber,
                    currentQuestionIndex,
                    firstResponse || "",
                    eventId,
                );

                // Handle navigation immediately (business logic)
                if (currentQuestionIndex < SIGNUP_QUESTIONS.length - 1) {
                    setCurrentQuestionIndex((prev: number) => prev + 1);
                    setCurrentStep(
                        SIGNUP_STEPS[
                            `QUESTION${currentQuestionIndex + 2}` as keyof typeof SIGNUP_STEPS
                        ],
                    );
                } else {
                    // For final question, handle completion with minimal delay
                    handleFinalQuestionCompletion();
                }
            } else {
                console.error(response.message);
            }
        } catch (error) {
            console.error("Error in signup process:", error);
        } finally {
            setButtonLoader(false);
        }
    };

    // Separate function to handle analytics events asynchronously
    const fireAnalyticsEvents = async (
        eventName: string,
        email: string | null,
        phoneNumber: string | null,
        questionIndex: number,
        firstResponse: string,
        eventId: string,
    ) => {
        try {
            // Initialize Branch first
            await initBranch();

            // Track current question event (non-blocking)
            // trackEventAsync(eventName, email || "", phoneNumber || "");
            setAnalyticsEvent("", eventName);

            // Handle last question additional events
            if (questionIndex === SIGNUP_QUESTIONS.length - 1) {
                // Track SignupComplete event (non-blocking)
                // trackEventAsync("SignupComplete", email || "", phoneNumber || "");
                setAnalyticsEvent("", "SignupComplete");

                // Determine and track final event
                let finalEvent: string = "";
                if (firstResponse === "Fixed deposits") {
                    finalEvent = "FD_Positive_Signup";

                    cleverTapTrackEvent(finalEvent, {
                        email: email || "",
                        phoneNumber: phoneNumber || "",
                    });
                } else if (firstResponse === "Digital Gold & Silver") {
                    finalEvent = "Bullion_Positive_Signup";
                    cleverTapTrackEvent(finalEvent, {
                        email: email || "",
                        phoneNumber: phoneNumber || "",
                    });
                }
                // else if (firstResponse === "Unlisted shares") {
                //     finalEvent = "Signup_US_Positive_signup";
                // } else if (firstResponse === "Loans") {
                //     finalEvent = "Signup_Negative_signup";
                // } else {
                //     finalEvent = "Signup_Positive_signup";
                // }

                if (firstResponse === "Unlisted shares") {
                    // Fire Facebook Pixel tracking (non-blocking)
                    import("react-facebook-pixel")
                        .then((module) => {
                            const ReactPixel = module.default;
                            ReactPixel.trackCustom("US_Positive_Signup", { eventID: eventId });
                        })
                        .catch((error) => {
                            console.error("Error loading Facebook Pixel:", error);
                        });

                    // Fire Meta conversion event (non-blocking)
                    sendMetaConversionEvent({
                        eventName: "US_Positive_Signup",
                        eventData: { userId: userDetails?.userId || "1234" },
                        eventId,
                    }).catch((error) => {
                        console.error("Error sending Meta conversion event:", error);
                    });

                    // Fire GTM event (non-blocking)
                    try {
                        trackGtmEvent();
                    } catch (error) {
                        console.error("Error tracking GTM event:", error);
                    }

                    // Fire Branch event based on platform (non-blocking)
                    // const isIosOrMac = () => {
                    //     const userAgent = window.navigator.userAgent;
                    //     return /iPhone|iPad|iPod|Macintosh|Mac OS X/i.test(userAgent);
                    // };

                    cleverTapTrackEvent("US_Positive_signup", {
                        email: email || "",
                        phoneNumber: phoneNumber || "",
                    });

                    initBranch()
                        .then(() => {
                            // const branchEventName = isIosOrMac()
                            //     ? "US_Positive_signup_IOS"
                            //     : "US_Positive_signup";
                            trackEventAsync("US_Positive_signup", email || "", phoneNumber || "");
                        })
                        .catch((error) =>
                            console.error("Error initializing Branch for platform event:", error),
                        );
                }

                // Track final event (non-blocking)
                if (finalEvent && finalEvent.length > 0) {
                    trackEventAsync(finalEvent, email || "", phoneNumber || "");
                }
                setAnalyticsEvent(window.location.origin, finalEvent);
            }
        } catch (error) {
            console.error("Error in analytics tracking:", error);
            // Don't throw - we don't want analytics failures to affect business logic
        }
    };

    // Wrapper to make trackEvent non-blocking
    const trackEventAsync = (eventName: string, email: string, phoneNumber: string) => {
        trackEvent(eventName, email, phoneNumber)
            .then(() => {
                console.log(`Successfully tracked event: ${eventName}`);
            })
            .catch((error: Error) => {
                console.error(`Error tracking event ${eventName}:`, error);
            });
    };

    // Handle final question completion with minimal blocking
    const handleFinalQuestionCompletion = () => {
        // Small delay to ensure the most critical final events are sent
        // But much shorter than before (200ms instead of 1000ms)
        setTimeout(() => {
            const redirectTo = getRedirectTo() ?? redirectUrl;

            if (isValidRedirectTo(redirectTo)) {
                clearRedirectTo();
                window.location.href = String(redirectTo);
            } else {
                redirectToDomain();
            }
        }, 200); // Reduced from 1000ms to 200ms
    };
    const handlePhoneVerify = () => {
        cleverTapTrackEvent("Sign_up_Mobile_Number", {
            email: email || "",
            phoneNumber: phoneNumber || "",
        });

        // initBranch()
        //     .then(() => {
        //         trackEvent("Sign_up_Mobile_Number", email || "", phoneNumber || "");
        //     })
        //     .catch((error) => console.error("Error initializing Branch:", error));
        setAnalyticsEvent(window.location.origin, "Sign_up_Mobile_Number");

        setViaPhone(true);
        setIsCarouselFrozen(true);
        loginRegisterWithOtp();
    };

    const resendOtp = async () => {
        if (currentStep === SIGNUP_STEPS.PHONEOTP && isNewUser) {
            // const token = await generateRecaptchToken("resendOtp");
            const token = await recaptchaGateway("resendOtp");
            if (!token) {
                setOtpErrorMessage("Failed to generate token");
                return;
            }

            let payload = getAuthPayload();

            payload = {
                ...payload,
                captchaToken: token ? token : "",
                version: process.env.APP_RECAPTCHA_VERSION_OVERRIDE === "v2" ? "v2" : "v3",
            };

            const response = await authService.resendOtpApi(payload);

            if (response && !response?.success && response?.message === 9 && response?.data) {
                setOtpErrorMessage(response?.data ?? "Too many attempts. Please try again later.");
            } else if (response && !response?.success && response?.message === 3) {
                setOtpErrorMessage(response?.data);
            }
        } else {
            loginFlow();
        }
    };

    const handlePhoneOtp = (otp: string) => {
        cleverTapTrackEvent("Sign_up_Mobile_Number_OTP", {
            email: email || "",
            phoneNumber: phoneNumber || "",
        });

        // Instead of setting state and then immediately using it,
        // pass the otp value directly to the verification function
        // initBranch()
        //     .then(() => {
        //         trackEvent("Sign_up_Mobile_Number_OTP", email || "", phoneNumber || "");
        //     })
        //     .catch((error) => console.error("Error initializing Branch:", error));
        setAnalyticsEvent(window.location.origin, "Sign_up_Mobile_Number_OTP");
        if (isNewUser) {
            verifyWithVerifyApi(otp);
        } else {
            verifyWithLoginApi(otp); // Pass otp directly
        }
    };

    async function handleEmailOtp(otp: string) {
        cleverTapTrackEvent("Signup_manual_email_OTP", {
            email: email || "",
            phoneNumber: phoneNumber || "",
        });

        // initBranch()
        //     .then(() => {
        //         trackEvent("Signup_manual_email_OTP", email || "", phoneNumber || "");
        //     })
        //     .catch((error) => console.error("Error initializing Branch:", error));
        setAnalyticsEvent(window.location.origin, "Signup_manual_email_OTP");
        try {
            setButtonLoader(true); // Disable button during OTP verification

            const payload: Payload = {
                userSwitch: true,
                otp: otp,
                name: name,
                email: email,
            };

            if (utmSource && Object.keys(utmSource)?.length > 0) {
                Object.entries(utmSource).forEach(([key, value]) => {
                    // if (value !== null) {
                    payload[String(key)] = value;
                    // }
                });
            }

            const response = await authService.userEmailVerifyPost(payload);

            if (response && !response.success) {
                setOtpErrorMessage(response.msg);
                return;
            }

            if (response && response.success) {
                setCurrentStep(SIGNUP_STEPS.QUESTION1);
                setIsLoggedin(true);
                // window.location.href = currentDomain + "/platform"; // || "/#/incredmoney-dashboard";
            }
        } catch (err) {
            console.error(err); // Log any errors
        } finally {
            setButtonLoader(false); // Enable button after process completion
        }
    }

    async function verifyWithVerifyApi(currentOtp: string) {
        try {
            setButtonLoader(true); // Disable button during verification

            const response = await authService.verifyOtp(await getVerifyPayload(currentOtp));

            if (response && !response.success) {
                setOtpErrorMessage(response.msg);
                return;
            }

            if (response && !response.success && response.data !== "") {
                setOtpErrorMessage(
                    response?.data ?? "Maximum limit reached. Please try again later.",
                );
                return;
            }

            if (response && response.success) {
                setOtpErrorMessage("");
                // toast.success(`${viaPhone ? 'Mobile number' : 'Email'} verified successfully.`);

                if (isNewUser) {
                    setPendingAuthTrack({
                        type: "signup",
                        method: viaPhone ? "phone" : "email",
                    });
                }

                /* 
                    Based on the response of /getUserEmail the next flow is decided.
                    i.e. whether to verify the email/update name/redirect the user to dashboard.
                    By changing the login status, the state change causes the
                    useEffect() to execute and /getUserEmail is called. This process is state lifted.
                */
                if (!isLoggedin) {
                    setIsLoggedin(true);
                } else {
                    redirectToDomain();
                }
            }
        } catch (err) {
            console.error(err);
        } finally {
            setButtonLoader(false); // Enable button after process completion
        }
    }

    async function getVerifyPayload(currentOtp: string, utmSource?: UtmParams) {
        // Accept otp as parameter
        const source = viaPhone
            ? isNewUser
                ? SIGNUP_TYPE.MOBILE_SIGNUP
                : SIGNUP_TYPE.MOBILE_LOGIN
            : isNewUser
              ? undefined
              : SIGNUP_TYPE.EMAIL_OTP_LOGIN;

        const commonParams = viaPhone
            ? { cCode, otp: currentOtp, mobile: phoneNumber, source } // Use passed otp
            : { email: phoneNumber, otp: currentOtp, source }; // Use passed otp

        let payload: Payload = {};

        if (utmSource && Object.keys(utmSource)?.length > 0) {
            Object.entries(utmSource).forEach(([key, value]) => {
                // if (value !== null) {
                payload[String(key)] = value;
                // }
            });
        }

        // const token = await generateRecaptchToken("verifyUser");
        const token = await recaptchaGateway("verifyUser");
        if (!token) {
            setOtpErrorMessage("Failed to generate token");
            throw new Error("Failed to generate token"); // ⬅ throw instead of return
        }

        payload = {
            ...payload,
            captchaToken: token ? token : "",
            version: process.env.APP_RECAPTCHA_VERSION_OVERRIDE === "v2" ? "v2" : "v3",
        };

        return {
            ...commonParams,
            ...payload,
            login: isNewUser || undefined,
        };
    }

    async function verifyWithLoginApi(currentOtp: string) {
        try {
            setButtonLoader(true); // Disable button during verification

            const response = await authService.verifyOtpWithLogin(
                await getVerifyPayload(currentOtp, utmSource),
            );

            if (response && !response.success) {
                if (typeof response?.data === "string") {
                    setOtpErrorMessage(
                        response.data ?? "The OTP entered is invalid. Please generate a new OTP",
                    );
                } else if (response.message === 3) {
                    setOtpErrorMessage("The OTP entered is invalid. Please generate a new OTP");
                }
                // setOtpErrorMessage(response.msg);
                return;
            }

            if (response && response.success) {
                localStorage.setItem("token", response.token);
                setOtpErrorMessage("");
                setPendingAuthTrack({
                    type: "login",
                    method: viaPhone ? "phone" : "email",
                });

                if (!isLoggedin) {
                    setIsLoggedin(true);
                    setHideOtpScreen(true);
                } else {
                    setOtpErrorMessage("");
                    const redirectTo = getRedirectTo();
                    if (isValidRedirectTo(redirectTo)) {
                        clearRedirectTo();
                        window.location.href = String(redirectTo);
                        return;
                    } else {
                        redirectToDomain();
                        return;
                    }
                }
            } else {
                if (response.message === 3 || response.message === 10) {
                    setResetOtp(true);
                    setOtpErrorMessage("The OTP entered is invalid. Please generate a new OTP");
                } else if (response.message === 8) {
                    setOtpErrorMessage(
                        response.message ?? "The OTP entered is invalid. Please generate a new OTP",
                    );
                }
            }
        } catch (err) {
            console.error(err);
        } finally {
            setButtonLoader(false); // Enable button after process completion
        }
    }

    function getAuthPayload(isLogin?: boolean): AuthPayload | OTPAuthPayload {
        let payload: Payload = {};

        if (currentStep === SIGNUP_STEPS.PHONE) {
            payload = {
                cCode: "91",
                mobile: phoneNumber,
                source: isLogin ? SIGNUP_TYPE.MOBILE_LOGIN : SIGNUP_TYPE.MOBILE_SIGNUP,
            };
        } else if (currentStep === SIGNUP_STEPS.PHONEOTP) {
            payload = {
                cCode: "91",
                mobile: phoneNumber,
                source: isLogin ? SIGNUP_TYPE.MOBILE_LOGIN : SIGNUP_TYPE.MOBILE_SIGNUP,
            };
        } else if (currentStep === SIGNUP_STEPS.EMAILOTP || currentStep === SIGNUP_STEPS.EMAIL) {
            payload = {
                email: email,
                source: SIGNUP_TYPE.EMAIL_OTP_LOGIN,
            };
        }

        // Add UTM params, filtering out null values
        Object.entries(utmSource || {}).forEach(([key, value]) => {
            // if (value !== null) {
            payload[String(key)] = value;
            // }
        });

        if (!isLogin) {
            payload.generateOtp = true;
        }

        return payload;
    }

    async function recaptchaGateway(action: string) {
        return await generateRecaptchToken(action); // fallback to normal v3
    }

    function generateRecaptchToken(action: string): Promise<string> {
        return new Promise((resolve, reject) => {
            // If reCAPTCHA is disabled, return a dummy token
            if (!recaptchaEnabled) {
                console.log("reCAPTCHA is disabled, returning dummy token");
                resolve("");
                return;
            }

            if (typeof executeRecaptcha !== "function") {
                reject(new Error("Execute recaptcha not yet available"));
                return;
            }

            executeRecaptcha(action)
                .then((token: string) => {
                    resolve(token);
                })
                .catch((error: Error) => {
                    console.error("reCAPTCHA error:", error);
                    reject(error);
                });
        });
    }

    async function loginFlow() {
        try {
            setButtonLoader(true); // Disable button
            let payload = getAuthPayload(true);
            // const token = await generateRecaptchToken("login");
            const token = await recaptchaGateway("login");
            if (!token) {
                setOtpErrorMessage("Failed to generate token");
                return;
            }
            payload = {
                ...payload,
                captchaToken: token ? token : "",
                version: process.env.APP_RECAPTCHA_VERSION_OVERRIDE === "v2" ? "v2" : "v3",
            };

            const response = await authService.loginWithOtp(payload);

            if (response && response.success && response.otp) {
                if (currentStep === SIGNUP_STEPS.PHONE) {
                    setCurrentStep(SIGNUP_STEPS.PHONEOTP);
                } else if (
                    currentStep === SIGNUP_STEPS.EMAIL ||
                    currentStep === SIGNUP_STEPS.EMAILOTP
                ) {
                    setCurrentStep(SIGNUP_STEPS.EMAILOTP);
                }
            } else if (response && !response.success && response?.message === 9 && response?.data) {
                setOtpErrorMessage(response?.data ?? "Too many attempts. Please try again later.");
            } else if (response && !response.success && response?.message === 3) {
                setOtpErrorMessage(response?.data);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setButtonLoader(false); // Enable button after completion
        }
    }

    async function loginRegisterWithOtp() {
        try {
            setButtonLoader(true); // Disable button
            let payload = getAuthPayload();
            // const token = await generateRecaptchToken("register");
            const token = await recaptchaGateway("register");
            if (!token) {
                setOtpErrorMessage("Failed to generate token");
                return;
            }

            payload = {
                ...payload,
                captchaToken: token ? token : "",
                version: process.env.APP_RECAPTCHA_VERSION_OVERRIDE === "v2" ? "v2" : "v3",
            };

            const response = await authService.registerWithOtp(payload);
            localStorage.removeItem(SIGNUP_URL_PARAMS.UTM_ICM_PARAM);

            if (response && !response.success && response?.message === 9 && response?.data) {
                setOtpErrorMessage(response?.data ?? "Too many attempts. Please try again later.");
                return;
            }

            if (!response || !response.success) {
                console.error(response.message);
                return;
            }

            if (
                (response.message === 1 && response.otp) ||
                (response.message === 2 && response.sendOtp)
            ) {
                setIsNewUser(true);
                setCurrentStep(SIGNUP_STEPS.PHONEOTP);
            } else {
                loginFlow();
            }
        } catch (err) {
            console.error(err);
        } finally {
            setButtonLoader(false); // Enable button after completion
        }
    }

    const editPhoneNumber = () => {
        setIsNewUser(false);
        setPhoneNumber("");
        setOtpErrorMessage("");
        setCurrentStep(SIGNUP_STEPS.PHONE);
        setIsCarouselFrozen(false);
    };

    const editEmailId = () => {
        // setEmail('');
        setOtpErrorMessage("");
        setCurrentStep(SIGNUP_STEPS.EMAIL);
    };

    async function handleEmailVerify() {
        try {
            setButtonLoader(true); // Disable button during the process

            // Validate the name and email using Zod schema
            nameValidation.parse(name); // Validate name
            emailValidation.parse(email); // Validate email

            const cleanName = (name: string): string => {
                // Trim leading/trailing whitespace and replace multiple spaces with a single space
                return name.trim().replace(/\s+/g, " ");
            };

            const payload: Payload = {
                email: email,
                name: cleanName(name),
                refCode: urlRefCode || "",
            };

            if (utmSource && Object.keys(utmSource)?.length > 0) {
                Object.entries(utmSource).forEach(([key, value]) => {
                    // if (value !== null) {
                    payload[String(key)] = value;
                    // }
                });
            }

            cleverTapTrackEvent("Signup_manual_email", {
                email: email || "",
                phoneNumber: phoneNumber || "",
            });

            // initBranch()
            //     .then(() => {
            //         trackEvent("Signup_manual_email", email || "", phoneNumber || "");
            //     })
            //     .catch((error) => console.error("Error initializing Branch:", error));
            setAnalyticsEvent(window.location.origin, "Signup_manual_email");

            const response = await authService.updateEmail(payload);

            if (response && !response.success) {
                setEmailError(response.message);
                return;
            }

            // 0 - nothing is set
            // 1 - email is set
            // 2 - name is set
            // 3 - both email & name is set

            if (response && response.success /* && response.code !== 2 */) {
                sendVerificationCode();
            } /* else if (response && response.success && response.code === 2) {
                // Redirect if response code is 2
                redirectToDomain();
            } */
        } catch (error) {
            if (error instanceof z.ZodError) {
                // Handle validation errors and display them
                error.errors.forEach((err) => {
                    if (err.message.toLowerCase().includes("name")) {
                        setNameError(err.message); // Set error for name
                    }
                    if (err.message.toLowerCase().includes("email")) {
                        setEmailError(err.message); // Set error for email
                    }
                });
            } else {
                console.error(error); // Log unexpected errors
            }
        } finally {
            setButtonLoader(false); // Enable button after process completion
        }
    }

    function sendVerificationCode() {
        authService.userEmailVerifyGet().then((response) => {
            if (response && !response.success) {
                setEmailError(
                    response.message || response.msg || "Error sending verification code",
                );
            }
            if (response && response.success) {
                // toast.success("OTP sent to your email successfully");
                setCurrentStep(SIGNUP_STEPS.EMAILOTP);
            } else if (response && !response.success && response.msg === "email already verified") {
                if (userDetails && !userDetails.phoneVerified) {
                    // setPhoneVerified(true);
                    return;
                }
                redirectToDomain();
            }
        });
    }

    // Render different components based on the current step
    const renderCurrentStep = () => {
        switch (currentStep) {
            case SIGNUP_STEPS.PHONE:
                return (
                    <PhoneInput
                        enableAutoSubmit={false}
                        phoneNumber={phoneNumber}
                        setPhoneNumber={setPhoneNumber}
                        handlePhoneVerify={handlePhoneVerify}
                        buttonLoader={buttonLoader}
                        otpErrorMessage={otpErrorMessage}
                        setOtpErrorMessage={setOtpErrorMessage}
                    />
                );

            case SIGNUP_STEPS.EMAILOTP:
            case SIGNUP_STEPS.PHONEOTP:
                return currentStep === SIGNUP_STEPS.PHONEOTP && hideOtpScreen ? (
                    <LoginLoader />
                ) : (
                    <OtpInput
                        enableAutoRead={false}
                        enableAutoSubmit={false}
                        handlePhoneOtp={handlePhoneOtp}
                        phoneNumber={phoneNumber}
                        resetOtp={resetOtp}
                        editPhoneNumber={editPhoneNumber}
                        otpType={currentStep}
                        email={email}
                        editEmailId={editEmailId}
                        handleEmailOtp={handleEmailOtp}
                        resendOtp={resendOtp}
                        otpErrorMessage={otpErrorMessage}
                        setOtpErrorMessage={setOtpErrorMessage}
                        buttonLoader={buttonLoader}
                    />
                );

            case SIGNUP_STEPS.EMAIL:
                return (
                    <DetermineEmailAuth
                        setEmail={setEmail}
                        email={email}
                        urlRefCode={urlRefCode || ""}
                        setName={setName}
                        name={name}
                        handleEmailVerify={handleEmailVerify}
                        handleGoogleSignUp={handleGoogleSignUp}
                        setNameError={setNameError}
                        setEmailError={setEmailError}
                        emailError={emailError} // Pass email error to child
                        nameError={nameError} // Pass name error to child
                        buttonLoader={buttonLoader}
                    />
                );

            case SIGNUP_STEPS.QUESTION1:
            case SIGNUP_STEPS.QUESTION2:
            case SIGNUP_STEPS.QUESTION3:
                return (
                    <QuestionsInput
                        question={SIGNUP_QUESTIONS[currentQuestionIndex]}
                        onSubmit={handleQuestionSubmit}
                        isMultiSelect={currentQuestionIndex === 2} // last question will be multiselect
                        isLastQuestion={currentQuestionIndex === SIGNUP_QUESTIONS.length - 1}
                        buttonLoader={buttonLoader}
                    />
                );

            default:
                return null;
        }
    };

    function LoginLoader() {
        return (
            <div className="m-auto flex w-full flex-col items-center justify-center">
                <LoadingIcon className="m-auto h-10 w-10 animate-spin" />
                <p className="mb-2">Please wait, checking your login status!</p>
            </div>
        );
    }

    return (
        <div className="flex min-h-dvh w-full">
            <div
                className="relative hidden w-[45%] justify-center bg-brand-primary-500 bg-cover bg-center bg-no-repeat md:flex"
                style={{ backgroundImage: `url(${loginBg.src})` }}
            >
                <LoginImageCarousel isFrozen={isCarouselFrozen} />
            </div>
            <div
                className={`h-dvh w-full bg-center px-6 pt-6 md:m-auto md:h-auto md:w-[519px] md:px-s14 md:py-s13 ${checkLogin ? "min-h-[500px]" : ""} ${"bg-cover md:bg-none"}`}
            >
                {/* <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.APP_RECAPTCHA_KEY_V2!}
                    size="invisible"
                />
                {/* <Image
                    src={`https://assets.incredmoney.com/images/webp/incred-money.webp`}
                    alt="biddeasy-logo"
                    height={119.47}
                    width={30.75}
                    onClick={() => window.location.href = `/`}
                    className="h-[28.60px] w-[113.27px] cursor-pointer md:h-[30.75px] md:w-[119.47px] block md:hidden mb-s7"
                /> */}
                {currentStep === SIGNUP_STEPS.PHONE && <MobileImageCarousel />}
                {checkLogin ? <LoginLoader /> : renderCurrentStep()}
            </div>
        </div>
    );
};

export default LoginContainer;
